import React, { useEffect, useState } from "react";
import PlacesInput from "../../UI/GooglePlacesAutocomplete/PlacesInput";
import {
  ModalCheck,
  ModalField,
  ModalFields,
  ModalForm,
  ModalInput,
} from "../../../design-system/patterns/modal-form";

/**
 * Saved address, add and edit. One of the nine modal forms.
 *
 * Validation, the Google Places component fill-down and the payload are
 * unchanged. What went is `AddressFormModal.css`, the `.required` asterisks and
 * the `.error-text` spans.
 *
 * The asterisks went for a reason rather than for tidiness: every field on this
 * form except line 2 is required, so marking the required ones marks almost all
 * of them and tells the user nothing. The library's rule is the inverse, mark
 * the OPTIONAL case, which is why line 2 is the only field carrying a marker.
 */

const AddressFormModal = ({
  open = true,
  initial = null,
  onSubmit,
  onCancel,
  submitting = false,
}) => {
  const [formData, setFormData] = useState({
    title: initial?.title || "",
    address_line1: initial?.address_line1 || "",
    address_line2: initial?.address_line2 || "",
    city: initial?.city || "",
    province: initial?.province || "",
    postal_code: initial?.postal_code || "",
    is_default: initial?.is_default || false,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initial) {
      setFormData({
        title: initial.title || "",
        address_line1: initial.address_line1 || "",
        address_line2: initial.address_line2 || "",
        city: initial.city || "",
        province: initial.province || "",
        postal_code: initial.postal_code || "",
        is_default: initial.is_default || false,
      });
    }
  }, [initial]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePlaceSelect = (placeDetails) => {
    if (!placeDetails || !placeDetails.addressComponents) return;

    const components = placeDetails.addressComponents;
    let city = "";
    let province = "";
    let postalCode = "";

    components.forEach((component) => {
      const types = component.types;

      if (types.includes("postal_code")) {
        postalCode = component.long_name;
      }

      if (types.includes("locality") || types.includes("sublocality_level_1")) {
        city = component.long_name;
      }

      if (types.includes("administrative_area_level_1")) {
        province = component.long_name;
      }
    });

    setFormData((prev) => ({
      ...prev,
      address_line1: placeDetails.address || prev.address_line1,
      city: city || prev.city,
      province: province || prev.province,
      postal_code: postalCode || prev.postal_code,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Address title is required";
    }

    if (!formData.address_line1.trim()) {
      newErrors.address_line1 = "Address line 1 is required";
    }

    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    if (!formData.province.trim()) {
      newErrors.province = "Province is required";
    }

    if (!formData.postal_code.trim()) {
      newErrors.postal_code = "Postal code is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <ModalForm
      open={open}
      title={initial ? "Edit address" : "Add new address"}
      onClose={onCancel}
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel={initial ? "Update address" : "Add address"}
      submittingLabel={initial ? "Updating" : "Adding"}
    >
      <ModalFields cols={2}>
        <ModalField label="Address title" error={errors.title} full>
          <ModalInput
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Home, Work, Office"
            disabled={submitting}
          />
        </ModalField>

        <ModalField label="Address line 1" error={errors.address_line1} full>
          <PlacesInput
            name="address_line1"
            value={formData.address_line1}
            onChange={handleChange}
            onPlaceSelect={handlePlaceSelect}
            placeholder="Start typing your address"
            error={Boolean(errors.address_line1)}
          />
        </ModalField>

        <ModalField label="Address line 2" optional full>
          <ModalInput
            name="address_line2"
            value={formData.address_line2}
            onChange={handleChange}
            placeholder="Apartment, suite, unit"
            disabled={submitting}
          />
        </ModalField>

        <ModalField label="City" error={errors.city}>
          <ModalInput
            name="city"
            value={formData.city}
            onChange={handleChange}
            disabled={submitting}
          />
        </ModalField>

        <ModalField label="Province" error={errors.province}>
          <ModalInput
            name="province"
            value={formData.province}
            onChange={handleChange}
            disabled={submitting}
          />
        </ModalField>

        <ModalField label="Postal code" error={errors.postal_code}>
          <ModalInput
            name="postal_code"
            value={formData.postal_code}
            onChange={handleChange}
            inputMode="numeric"
            disabled={submitting}
          />
        </ModalField>
      </ModalFields>

      <ModalCheck
        name="is_default"
        checked={formData.is_default}
        onChange={handleChange}
        disabled={submitting}
      >
        Set as default address
      </ModalCheck>
    </ModalForm>
  );
};

export default AddressFormModal;
